/**
 * outcomeMeta for the current env (#1809, WS_DESIGN_1809 W4). One REST read at start, then `outcomeMetaUpdates` events keep the
 * same query cache entry live (catalogEvents.ts), so every reader sees markets leave and arrive at once.
 *
 * REST reads, all from useHoldOutcomeMeta: the first, one after each WS reconnect, and one every CATALOG_CHECK_MS as a safety
 * net, since the stream has no sequence numbers. A read that differs from the held catalog is counted
 * (window.__hip4Catalog), logged, and the snapshot is taken. A reader that finds data never reads.
 *
 * The check is not TanStack's refetchInterval: every cache update restarts that timer, and a frame comes about once a minute.
 */
import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { EnvName } from '../config/env';
import { buildRegistry, templateId } from '../describe/templates';
import { hlQueryKey, useHlEnv, useInfoClient, useOnWsReconnect, useSubscription } from '../hl/react';
import type { OutcomeMetaResponse } from '../hl/types';
import { tabAsleep } from '../hl/tabSleep';
import { backgroundPacer } from '../hl/weightPacer';
import { CatalogFeed, driftCount, type CatalogStats } from './catalogEvents';

const UPDATES_SUB = { type: 'outcomeMetaUpdates' } as const;
/** Weight of one outcomeMeta read. */
const OUTCOME_META_WEIGHT = 20;
/** The drift check (WS_DESIGN_1809 section 5, item 6). */
export const CATALOG_CHECK_MS = 30 * 60_000;
/** Next try after a failed read of a held catalog, or after a frame this code could not apply. */
export const CATALOG_RETRY_MS = 60_000;

declare global {
  interface Window {
    /** Catalog counters by network, for a look in the console (#1809). */
    __hip4Catalog?: Partial<Record<EnvName, CatalogStats>>;
  }
}

const feeds = new WeakMap<QueryClient, Map<EnvName, CatalogFeed>>();
const retries = new WeakMap<CatalogFeed, ReturnType<typeof setTimeout>>();

/** The stream state for one client + env: it outlives any component. */
export function catalogFeed(qc: QueryClient, env: EnvName): CatalogFeed {
  let byEnv = feeds.get(qc);
  if (!byEnv) feeds.set(qc, (byEnv = new Map()));
  let feed = byEnv.get(env);
  if (!feed) {
    byEnv.set(env, (feed = new CatalogFeed()));
    (window.__hip4Catalog ??= {})[env] = feed.stats;
  }
  return feed;
}

const metaKey = (env: EnvName) => hlQueryKey(env, 'outcomeMeta');

function readNow(qc: QueryClient, env: EnvName): void {
  // cancelRefetch false: join a read already running.
  void qc.refetchQueries({ queryKey: metaKey(env), exact: true }, { cancelRefetch: false });
}

function readSoon(qc: QueryClient, env: EnvName, feed: CatalogFeed): void {
  if (retries.has(feed)) return;
  retries.set(
    feed,
    setTimeout(() => {
      retries.delete(feed);
      if (!tabAsleep()) readNow(qc, env); // Otherwise the return reads.
    }, CATALOG_RETRY_MS),
  );
}

/** Every observer shares this query function, so no path publishes a snapshot without the replay. Never stale by age. */
function useCatalog(enabled = true) {
  const env = useHlEnv();
  const info = useInfoClient();
  const qc = useQueryClient();
  return useQuery({
    queryKey: metaKey(env),
    queryFn: async ({ signal }): Promise<OutcomeMetaResponse> => {
      const feed = catalogFeed(qc, env);
      const key = metaKey(env);
      const heldNow = () => qc.getQueryData<OutcomeMetaResponse>(key);
      // A failed safety-net read must not turn a working catalog into an error page.
      const keepHeld = (held: OutcomeMetaResponse) => {
        feed.stats.failedReads++;
        readSoon(qc, env, feed);
        return held;
      };
      let snapshot: OutcomeMetaResponse;
      try {
        await backgroundPacer(info).take(OUTCOME_META_WEIGHT, { signal });
        snapshot = await info.outcomeMeta({ signal });
      } catch (e) {
        const held = heldNow();
        if (signal.aborted) throw e;
        if (held) return keepHeld(held);
        readSoon(qc, env, feed); // Nothing to show yet, and no timer of the query's own to try again.
        throw e;
      }
      const held = heldNow(); // After the await: frames that came during the read are in it, and in the replay.
      if (!Array.isArray(snapshot?.outcomes)) return held ? keepHeld(held) : snapshot;
      const r = feed.snapshot(held, snapshot, Date.now());
      if (r.diff && driftCount(r.diff) > 0) console.warn(`[catalog] ${r.reason} read differs from events, taking the snapshot`, r.diff);
      return r.meta;
    },
    staleTime: Infinity,
    enabled,
  });
}

const askedTemplates = new WeakMap<QueryClient, Set<string>>();

/** Templates are read on demand: when an event names one we do not know, once per name. */
function readTemplatesIfNew(qc: QueryClient, env: EnvName, rows: readonly { name: string }[]): void {
  const key = hlQueryKey(env, 'outcomeTemplates');
  const known = buildRegistry(env, qc.getQueryData<unknown[]>(key));
  let asked = askedTemplates.get(qc);
  if (!asked) askedTemplates.set(qc, (asked = new Set()));
  let read = false;
  for (const row of rows) {
    const id = templateId(row.name);
    if (id === null || known.has(id) || asked.has(`${env}:${id}`)) continue;
    asked.add(`${env}:${id}`);
    read = true;
  }
  if (read) void qc.invalidateQueries({ queryKey: key, exact: true }, { cancelRefetch: false });
}

/** Holds the catalog, its stream and its checks for the life of the page: GlobalStreams only. */
export function useHoldOutcomeMeta(): void {
  const env = useHlEnv();
  const qc = useQueryClient();
  const feed = catalogFeed(qc, env);
  useCatalog();

  useSubscription(UPDATES_SUB, (events) => {
    if (!Array.isArray(events) || events.length === 0) return;
    const key = metaKey(env);
    const held = qc.getQueryData<OutcomeMetaResponse>(key);
    const r = feed.frame(held, events, Date.now());
    if (held && r.meta !== held) qc.setQueryData(key, r.meta);
    if (r.unknown.length > 0) {
      console.warn('[catalog] events not understood, reading a snapshot', r.unknown);
      readSoon(qc, env, feed);
    }
    readTemplatesIfNew(qc, env, [...r.outcomes, ...r.questions]);
  });

  useOnWsReconnect('outcomeMeta', () => {
    feed.nextReason = 'reconnect';
    readNow(qc, env);
  });

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== 'hidden') readNow(qc, env);
    }, CATALOG_CHECK_MS);
    return () => {
      clearInterval(id);
      clearTimeout(retries.get(feed));
      retries.delete(feed);
    };
  }, [qc, env, feed]);
}

export function useOutcomeMeta(enabled = true) {
  return useCatalog(enabled);
}

/** The catalog plus outcomes that have left it, for names only (toasts, position rows). Never for what is listed or tradable. */
export function useOutcomeMetaForNames(enabled = true): OutcomeMetaResponse | undefined {
  const env = useHlEnv();
  const qc = useQueryClient();
  const data = useCatalog(enabled).data;
  return useMemo(() => catalogFeed(qc, env).withRetired(data), [qc, env, data]);
}
